import {
    Button,
    Drawer,
    Heading,
    SelectField,
    TextField,
    TextPassage,
    Row,
} from '@petsmart-ui/sparky';
import type {
    SddForm,
    AddSddAddrFormProps,
    Errors as ErrorType,
} from '@app/pdp/components/AddToCart/DeliveryMethods/types';
import {
    useState,
    type ChangeEvent,
    type ChangeEventHandler,
    type FormEvent,
    useEffect,
} from 'react';
import { getLocale, isString, zipValidation } from '@libs/core';
import { ExperianConfidence } from '@libs/experian';
import { validateAddress } from '@libs/experian/server';
import { dataLayerPush } from '@libs/analytics';
import { useProductDetails } from '@app/pdp/hooks/useProductDetails';
import { isFunction } from 'lodash';

import { localize } from './constants';
import StreetAddressInput from './StreetAddressInput';
import ErrorMessage from './components/ErrorAlert';

type getFormFieldProps = {
    onChange: ChangeEventHandler<Element>;
    onBlur?: ChangeEventHandler<Element>;
    field: keyof SddForm;
    validationMsg?: string | null | ((value: string | boolean) => string);
    label: string;
    halfSize?: boolean;
};

const defaultSddFormState: SddForm = {
    street: '',
    complement: '',
    zip: '',
    state: '',
    city: '',
    isValid: true,
};

const defaultSddFormIsValidState: Record<keyof SddForm, boolean> = {
    street: true,
    complement: true,
    zip: true,
    state: true,
    city: true,
    isValid: true,
};

export default function AddSddAddrForm({
    isAvailable,
    isDrawerOpen,
    closeDrawer,
    sddAddress,
    onAddressSubmit,
}: AddSddAddrFormProps) {
    const [showComplement, setShowComplement] = useState(
        !!(sddAddress && sddAddress.complement)
    );

    const { selectedProduct } = useProductDetails();
    const [sddForm, setSddForm] = useState<SddForm>(
        sddAddress ?? defaultSddFormState
    );

    const [errorType, setErrorType] = useState<ErrorType | null>(null);

    const [sddFormIsValid, setSddFormIsValid] = useState<
        Record<keyof SddForm, boolean>
    >(defaultSddFormIsValidState);

    useEffect(() => {
        if (!isAvailable) {
            if (errorType) {
                setErrorType(null);
            } else {
                setErrorType('estimationValidation');
            }
        }
        if (isAvailable && errorType === 'estimationValidation') {
            setErrorType(null);
        }
        return () => {
            setErrorType(null);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run once
    }, []);

    const toggleComplement = () => setShowComplement(!showComplement);

    const {
        streetAddressValidation,
        stateProvinceSelection,
        stateProvinceLabel,
        stateProvinceValidation,
        zipPostalCode,
        zipPostalCodeValidation,
        zipPostalCodeValidationInvalid,
    } = localize[getLocale()];

    const sddOnChange = ({
        target: { name, value },
    }: ChangeEvent<HTMLInputElement>) => {
        setSddForm(oldState => ({ ...oldState, [name]: value }));
        setSddFormIsValid(oldState => ({ ...oldState, [name]: !!value }));
    };

    const sddOnBlur = ({
        target: { name, value },
    }: ChangeEvent<HTMLInputElement>) => {
        if (name === 'zip') {
            const isValid = zipValidation(value);
            setSddForm(oldState => ({ ...oldState, [name]: value }));
            setSddFormIsValid(oldState => ({ ...oldState, [name]: isValid }));
        }
    };

    const isSddAddressEmpty = (address: SddForm) =>
        /*
            isValid excluded because it should lead to a different error
            complement excluded because is not required to have a value
            rest of address keys are evaluated and accumulated through reduce fn,
            if any value is undefined or empty, function will return true
        */
        Object.keys(address)
            .filter(key => key !== 'isValid' && key !== 'complement')
            .reduce((acc, current) => {
                const emptyValue = !address[current as keyof SddForm];
                return acc || emptyValue;
            }, false);

    const sddIsInvalid = () =>
        Object.keys(sddFormIsValid).some(key =>
            ['complement', 'isValid'].includes(key)
                ? false
                : !sddFormIsValid[key as keyof SddForm]
        );

    const sddOnSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.stopPropagation();
        e.preventDefault();
        setErrorType(null);

        const validation: { [key: string]: boolean } = {};

        for (const key in sddForm) {
            if (key === 'zip') {
                validation[key] = zipValidation(sddForm[key]);
                continue;
            }
            validation[key] = !!sddForm[key as keyof SddForm];
        }

        setSddFormIsValid(validation as Record<keyof SddForm, boolean>);

        const isAddressEmpty = isSddAddressEmpty(sddForm);

        const isAddressInvalid = sddIsInvalid();
        if (isAddressEmpty || isAddressInvalid) {
            setErrorType(isAddressEmpty ? 'emptyFields' : 'invalidFields');
            return;
        }

        const streetName = `${sddForm.street},${sddForm.complement}, ${sddForm.city}, ${sddForm.state}, ${sddForm.zip}`;

        const validationResponse = await validateAddress(streetName);

        const hasEmptyFields =
            !sddForm.street || !sddForm.state || !sddForm.city || !sddForm.zip;

        let isValid = hasEmptyFields
            ? true
            : validationResponse?.result.confidence ===
              ExperianConfidence.VERIFIED_MATCH;

        isValid = await onAddressSubmit(sddForm, true);

        if (!isValid) {
            setErrorType('estimationValidation');
        }

        dataLayerPush({
            event: 'same_day_delivery_check',
            product_id: selectedProduct.id,
        });

        if (isValid) {
            closeDrawer(false);
        }
    };

    const getErrorMessage = (
        message: string | null | ((value: string | boolean) => string),
        field: keyof SddForm
    ) => {
        if (isFunction(message)) {
            return message(sddForm[field]);
        }
        return message;
    };

    const getFormTextField = ({
        field,
        label,
        onChange,
        onBlur,
        validationMsg = null,
        halfSize = false,
    }: getFormFieldProps) => {
        const message = getErrorMessage(validationMsg, field);
        const hasError = message && !sddFormIsValid[field];

        return (
            <TextField
                onChange={onChange}
                onBlur={onBlur}
                data-cs-mask
                name={field}
                data-testid={`sdd-${field}`}
                className={`sdd-form-input${halfSize ? '-half' : ''}`}
                label={label}
                isError={validationMsg ? !sddFormIsValid[field] : false}
                value={(sddForm[field] as string) ?? ''}
                id={field}
                type="text"
                fieldNote={hasError ? message : null}
            />
        );
    };

    const getFormSelect = ({
        field,
        validationMsg = null,
    }: getFormFieldProps) => {
        const message = getErrorMessage(validationMsg, field);
        const hasError = message && !sddFormIsValid[field];

        return (
            <SelectField
                onChange={sddOnChange}
                data-cs-mask
                name={field}
                data-testid="sdd-state"
                className="sdd-form-input-half"
                label={stateProvinceLabel}
                isError={validationMsg ? !sddFormIsValid[field] : false}
                iconName={hasError ? 'exclamation-circle-fill' : undefined}
                id={field}
                items={stateProvinceSelection.map(({ abbr, name }) => ({
                    label: name,
                    value: abbr,
                    disabled: false,
                }))}
                value={sddForm[field] as string}
                fieldNote={hasError ? message : null}
                defaultValue={sddForm[field] as string}
            />
        );
    };

    return (
        <Drawer
            isActive={isDrawerOpen}
            onClose={() => {
                setErrorType(null);
                closeDrawer(sddIsInvalid());
                setSddForm(defaultSddFormState);
                setSddFormIsValid(defaultSddFormIsValidState);
            }}
            align="right"
            ariaLabelledBy="drawer-heading"
            data-testid="sdd-drawer"
        >
            <Drawer.Header>
                <Heading id="drawer-heading" tagName="h2" size="headline">
                    Same-day delivery
                </Heading>
            </Drawer.Header>
            <Drawer.Body>
                {errorType && <ErrorMessage {...{ errorType }} />}
                <TextPassage className="check-availability">
                    Please provide more details to confirm same-day delivery is
                    available in your area
                </TextPassage>
                <form id="sdd-form" onSubmit={sddOnSubmit}>
                    <Row className="form-row sdd-form-input__street">
                        <StreetAddressInput
                            sddForm={sddForm}
                            isError={!sddFormIsValid.street}
                            sddOnChange={sddOnChange}
                            validationMsg={
                                sddFormIsValid.street
                                    ? ''
                                    : streetAddressValidation
                            }
                        />
                    </Row>
                    <Row className="form-row">
                        {showComplement ? (
                            getFormTextField({
                                label: 'Apt/suite/other',
                                field: 'complement',
                                onChange: sddOnChange,
                            })
                        ) : (
                            <Button
                                iconName="plus"
                                data-testid="sdd-show-complement"
                                text="Add apt/suite/other"
                                variant="link"
                                onClick={toggleComplement}
                            />
                        )}
                    </Row>
                    <Row className="form-row">
                        {getFormTextField({
                            label: zipPostalCode,
                            halfSize: true,
                            onChange: sddOnChange,
                            onBlur: sddOnBlur,
                            field: 'zip',
                            validationMsg: value => {
                                if (!value) {
                                    return zipPostalCodeValidation;
                                }
                                if (
                                    !sddFormIsValid.zip &&
                                    isString(value) &&
                                    !zipValidation(value)
                                ) {
                                    return zipPostalCodeValidationInvalid;
                                }
                                return zipPostalCodeValidation;
                            },
                        })}
                        {getFormSelect({
                            label: stateProvinceLabel,
                            halfSize: true,
                            onChange: sddOnChange,
                            field: 'state',
                            validationMsg: stateProvinceValidation,
                        })}
                    </Row>
                    <Row className="form-row">
                        {getFormTextField({
                            label: 'City',
                            onChange: sddOnChange,
                            field: 'city',
                            validationMsg: 'City is required',
                        })}
                    </Row>
                    <Row className="form-row">
                        <Button
                            fullWidth
                            text="Check address"
                            variant="primary"
                            type="submit"
                            data-testid="sdd-check-address"
                        />
                    </Row>
                </form>
            </Drawer.Body>
        </Drawer>
    );
}
